import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ARBEITSZEIT_HAUSMASS,
  DAUER_VORGABE,
  arbeitszeitDerWelt,
  arbeitszeitVon,
  type Dauerstufe,
} from '@netcon-work/shared'
import { useState } from 'react'
import { meineArbeitszeitQuery, workspacesQuery, type WorkspaceEintrag } from '../../lib/queries'
import { trpc } from '../../lib/trpc'
import { Button } from '../ui/primitives'

/**
 * **Die eigene Arbeitszeit einstellen** ([Z-19](../../../../../docs/phasen/zusatz-19-tagesplan.md)
 * AP 19.4).
 *
 * ## Warum hier und nicht in den Einstellungen
 *
 * Weil man sie **hier** vermisst. Im Kopf des Tagesplans stand bis zum 01.10.2026
 * „08:00–17:00" als unveränderliche Auskunft – und wer um halb sieben anfängt, liest dort jeden
 * Morgen eine Zahl, die nicht stimmt, ohne einen Weg zu sehen, sie zu ändern. Eine Einstellung,
 * die man an der Stelle ihrer Wirkung ändern kann, wird geändert; eine, die drei Klicks entfernt
 * in einer Liste wohnt, bleibt falsch stehen.
 *
 * ## Die Wochentage als sieben Schalter
 *
 * Montag zuerst, wie in der Datenbank (`1111100`). Sieben Knöpfe statt einer Auswahlliste, weil
 * die Frage *„arbeite ich samstags"* mit einem Blick zu beantworten sein muss – und weil
 * Teilzeit selten „Mo–Fr" heißt, sondern „Mo, Di, Do".
 *
 * **Zurücksetzen gibt es ausdrücklich**: `null` in allen vier Feldern heißt „keine eigene
 * Einstellung", und das ist etwas anderes als „acht bis siebzehn eingetragen". Ohne diesen Weg
 * bliebe die erste versehentliche Eingabe für immer stehen.
 *
 * **Der Knopf hieß erst „Hausmaß".** Nils, 01.10.2026: *„was heißt «Hausmaß» in dem Dialog?"* –
 * eine berechtigte Frage. Das Wort steht im Quelltext für den Vorgabewert und war dort richtig;
 * auf einem Knopf ist es ein Fachbegriff, den der Benutzer nicht gelernt hat. Jetzt steht da,
 * was passiert, und in Klammern, was danach gilt.
 */

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

/** `8.5` → `08:30` für das Eingabefeld. */
const alsUhrzeit = (stunden: number): string =>
  `${String(Math.floor(stunden)).padStart(2, '0')}:${String(Math.round((stunden % 1) * 60)).padStart(2, '0')}`

/** `08:30` → 510 Minuten. `null`, wenn das Feld leer oder unsinnig ist. */
function alsMinuten(wert: string): number | null {
  const [h, m] = wert.split(':').map(Number)
  if (h === undefined || m === undefined || !Number.isFinite(h) || !Number.isFinite(m)) return null
  return h * 60 + m
}

export function ArbeitszeitBlatt({ schliessen }: { schliessen: () => void }) {
  const qc = useQueryClient()
  const { data: felder } = useQuery(meineArbeitszeitQuery)
  const { data: welten = [] } = useQuery(workspacesQuery)

  /**
   * **Welche Zeit gerade bearbeitet wird** – `null` ist die allgemeine, sonst eine Arbeitswelt
   * (Nils, 01.10.2026: *„pro Workspace optional abweichendes festlegen"*).
   *
   * Die Welt-Zeit erbt feldweise von der allgemeinen: Wer in PRIVAT nur die **Tage** umstellt,
   * behält dort seine gewohnten Stunden. Deshalb zeigt das Formular beim Wechsel immer die
   * **wirksame** Zeit, nicht das leere Feld – man sieht, wogegen man ändert.
   */
  const [weltId, setWeltId] = useState<string | null>(null)
  const welt = welten.find((w) => w.id === weltId) ?? null
  const wirksam = welt ? arbeitszeitDerWelt(felder, welt) : arbeitszeitVon(felder)

  const [von, setVon] = useState(alsUhrzeit(wirksam.von))
  const [bis, setBis] = useState(alsUhrzeit(wirksam.bis))
  const [grundlast, setGrundlast] = useState(String(Math.round(wirksam.grundlast * 60)))
  const [tage, setTage] = useState(wirksam.tage)

  /** Beim Wechsel der Welt die Felder neu füllen – sonst schriebe man die alte Zeit in die neue Welt. */
  const wechseln = (ziel: string | null) => {
    const z = ziel
      ? arbeitszeitDerWelt(felder, welten.find((w) => w.id === ziel) ?? { id: ziel })
      : arbeitszeitVon(felder)
    setWeltId(ziel)
    setVon(alsUhrzeit(z.von))
    setBis(alsUhrzeit(z.bis))
    setGrundlast(String(Math.round(z.grundlast * 60)))
    setTage(z.tage)
  }

  const speichern = useMutation({
    mutationFn: (v: {
      arbeitVon: number | null
      arbeitBis: number | null
      grundlastMinuten: number | null
      arbeitstage: string | null
    }): Promise<unknown> =>
      weltId
        ? trpc.workspaces.arbeitszeitSetzen.mutate({
            workspaceId: weltId,
            arbeitVon: v.arbeitVon,
            arbeitBis: v.arbeitBis,
            arbeitstage: v.arbeitstage,
          })
        : trpc.people.arbeitszeitSetzen.mutate(v),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['people', 'meineArbeitszeit'] })
      await qc.invalidateQueries({ queryKey: ['workspaces'] })
      schliessen()
    },
  })

  const umschalten = (i: number) =>
    setTage((t) => t.slice(0, i) + (t[i] === '1' ? '0' : '1') + t.slice(i + 1))

  const minuten = Number(grundlast)
  const gueltig =
    alsMinuten(von) !== null &&
    alsMinuten(bis) !== null &&
    (alsMinuten(bis) ?? 0) > (alsMinuten(von) ?? 0) &&
    Number.isFinite(minuten) &&
    minuten >= 0

  return (
    <div
      className="unter-leiste fixed inset-x-0 bottom-0 z-[60] flex items-end justify-center bg-[rgb(23_22_20/0.28)] sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Arbeitszeit"
      onClick={schliessen}
    >
      <div
        /*
         * **Höhe begrenzen, Rumpf scrollen** – `max-h-[min(80vh,100%)]`, nicht `80vh`: Die
         * Abdunklung beginnt 48 px unter dem oberen Rand (`unter-leiste`), ein Blatt mit 80 vh
         * rägte unten heraus und sein Fuß wäre unerreichbar. Genau dieser Fehler steht in
         * `unter-leiste.test.ts` beschrieben (E-325).
         */
        className="flex max-h-[min(80vh,100%)] w-full max-w-[420px] flex-col rounded-t-card border border-line bg-surface shadow-lg sm:rounded-card"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="border-b border-line px-4 py-3 text-[14.5px] font-bold">
          Deine Arbeitszeit
        </h3>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3 text-[12.5px]">
          {/*
           * **Auf dem Telefon steht die Beschriftung über dem Feld, nicht daneben.**
           *
           * Nachgerechnet: 86 px Beschriftung + sieben Wochentagsknopfä 34 px + Lücken + Rand
           * sind **380 px** – ein iPhone SE hat 375, das kleinste Ziel aus Z-10 sogar nur 358.
           * Die Reihe wäre also ausgebrochen. Ab `sm` ist Platz, dort steht es wieder nebeneinander.
           */}
          <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center">
            <span className="text-ink2 sm:w-[86px]">gilt für</span>
            <span className="flex flex-wrap gap-1">
              <button
                type="button"
                aria-pressed={weltId === null}
                onClick={() => wechseln(null)}
                className={
                  weltId === null
                    ? 'cursor-pointer rounded-md bg-accent px-2 py-1 text-[11.5px] font-semibold text-white'
                    : 'cursor-pointer rounded-md border border-line2 px-2 py-1 text-[11.5px] text-ink2'
                }
              >
                überall
              </button>
              {welten.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  aria-pressed={weltId === w.id}
                  onClick={() => wechseln(w.id)}
                  className={
                    weltId === w.id
                      ? 'cursor-pointer rounded-md bg-accent px-2 py-1 text-[11.5px] font-semibold text-white'
                      : 'cursor-pointer rounded-md border border-line2 px-2 py-1 text-[11.5px] text-ink2'
                  }
                  title={
                    w.arbeitVon !== null || w.arbeitBis !== null || w.arbeitstage !== null
                      ? 'Hat eine eigene Zeit'
                      : 'Nimmt die allgemeine Zeit'
                  }
                >
                  {w.label}
                  {(w.arbeitVon !== null || w.arbeitBis !== null || w.arbeitstage !== null) && ' ·'}
                </button>
              ))}
            </span>
          </div>

          <label className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
            <span className="text-ink2 sm:w-[86px]">von – bis</span>
            <input
              type="time"
              value={von}
              onChange={(e) => setVon(e.target.value)}
              className="rounded-lg border border-line2 px-2 py-1"
            />
            <input
              type="time"
              value={bis}
              onChange={(e) => setBis(e.target.value)}
              className="rounded-lg border border-line2 px-2 py-1"
            />
          </label>

          <label
            className={
              weltId ? 'hidden' : 'flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2'
            }
          >
            <span className="text-ink2 sm:w-[86px]">Grundlast</span>
            <input
              type="number"
              min={0}
              max={720}
              step={15}
              value={grundlast}
              onChange={(e) => setGrundlast(e.target.value)}
              className="w-[80px] rounded-lg border border-line2 px-2 py-1 tabular-nums"
            />
            <span className="text-muted">Minuten am Tag für Mail, Telefon, Unterbrechung</span>
          </label>

          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
            <span className="text-ink2 sm:w-[86px]">Arbeitstage</span>
            <div className="flex flex-wrap gap-1">
              {WOCHENTAGE.map((name, i) => (
                <button
                  key={name}
                  type="button"
                  aria-pressed={tage[i] === '1'}
                  onClick={() => umschalten(i)}
                  className={
                    tage[i] === '1'
                      ? 'cursor-pointer rounded-md bg-accent px-2 py-1 text-[11.5px] font-semibold text-white'
                      : 'cursor-pointer rounded-md border border-line2 px-2 py-1 text-[11.5px] text-ink2'
                  }
                >
                  {name}
                </button>
              ))}
            </div>
          </div>

          <p className="text-[11.5px] text-muted">
            Gesetzliche Feiertage in NRW sind immer frei – auch an einem Tag, den du hier ankreuzt.
            {weltId
              ? ' Mail und Telefon werden einmal am Tag abgezogen, nicht je Arbeitswelt – das steht unter „überall".'
              : ''}
          </p>

          <DauernFelder weltId={weltId} welt={welt} />
        </div>

        <footer className="flex items-center justify-between border-t border-line px-4 py-2.5">
          <Button
            variant="ghost"
            disabled={speichern.isPending}
            onClick={() =>
              speichern.mutate({
                arbeitVon: null,
                arbeitBis: null,
                grundlastMinuten: null,
                arbeitstage: null,
              })
            }
            title={`Eigene Einstellung löschen – danach gilt wieder ${ARBEITSZEIT_HAUSMASS.von}–${ARBEITSZEIT_HAUSMASS.bis} Uhr, Mo–Fr`}
          >
            {/**
             * **Auf dem Telefon nur „Zurücksetzen"** (01.10.2026, im Gerätemodus gesehen).
             *
             * Ausgeschrieben braucht der Knopf dort **drei Zeilen** und drängt „Abbrechen" und
             * „Merken" an den Rand – der Fuß sah aus, als wäre der Rückweg die Hauptsache. Was
             * danach gilt, steht im `title` und ab `sm` wieder daneben.
             */}
            Zurücksetzen
            <span className="hidden sm:inline">
              {` (${ARBEITSZEIT_HAUSMASS.von}–${ARBEITSZEIT_HAUSMASS.bis} Uhr, Mo–Fr)`}
            </span>
          </Button>
          <span className="flex gap-2">
            <Button variant="ghost" onClick={schliessen}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              disabled={!gueltig || speichern.isPending}
              onClick={() =>
                speichern.mutate({
                  arbeitVon: alsMinuten(von),
                  arbeitBis: alsMinuten(bis),
                  grundlastMinuten: minuten,
                  arbeitstage: tage,
                })
              }
            >
              Merken
            </Button>
          </span>
        </footer>
      </div>
    </div>
  )
}

/**
 * **Wie lange eine Größe dauert** ([E-390](../../../../../docs/ENTSCHEIDUNGEN.md#e-390)).
 *
 * Nils, 01.10.2026: *„Die Größen sollten standardmäßig werte haben. und je nach workspace kann
 * man die überschreiben."*
 *
 * ## Warum hier im selben Blatt
 *
 * Aus demselben Grund wie die Arbeitszeit darüber: Man vermisst die Einstellung **dort, wo sie
 * wirkt**. Wer im Tagesplan sieht, dass „mittel" bei ihm eher zwei Stunden sind, soll es an Ort
 * und Stelle ändern können – und nicht erst eine Verwaltungsseite suchen, auf der er dann nicht
 * mehr weiß, welche Zahl ihn gestört hat.
 *
 * ## Die Dauern gehören der Welt, die Arbeitszeit der Person
 *
 * Deshalb sind sie **nur** bei gewählter Arbeitswelt änderbar und stehen unter „überall" nur als
 * Auskunft. Eine Größe ist eine gemeinsame Sprache: Ein „klein" in der Entwicklung muss für alle
 * dasselbe heißen, sonst sagt die Schätzung nichts mehr. Wie schnell **ich** arbeite, steht
 * dagegen oben in meinen Stunden.
 */
function DauernFelder({ weltId, welt }: { weltId: string | null; welt: WorkspaceEintrag | null }) {
  const qc = useQueryClient()
  const [werte, setWerte] = useState<Record<Dauerstufe, string>>({
    klein: '',
    mittel: '',
    gross: '',
    ohne: '',
  })
  const [bearbeitet, setBearbeitet] = useState<string | null>(null)

  /** Beim Wechsel der Welt neu füllen – sonst schriebe man die alten Zahlen in die neue Welt. */
  if (bearbeitet !== weltId) {
    setBearbeitet(weltId)
    setWerte({
      klein: welt?.dauerKlein ? String(welt.dauerKlein) : '',
      mittel: welt?.dauerMittel ? String(welt.dauerMittel) : '',
      gross: welt?.dauerGross ? String(welt.dauerGross) : '',
      ohne: welt?.dauerOhne ? String(welt.dauerOhne) : '',
    })
  }

  const speichern = useMutation({
    mutationFn: (v: Record<Dauerstufe, number | null>) =>
      trpc.workspaces.dauernSetzen.mutate({ workspaceId: weltId ?? '', ...v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspaces', 'meine'] }),
  })

  const STUFEN: { id: Dauerstufe; text: string }[] = [
    { id: 'klein', text: 'klein' },
    { id: 'mittel', text: 'mittel' },
    { id: 'gross', text: 'groß' },
    { id: 'ohne', text: 'ohne Angabe' },
  ]

  /** Leer heißt „Vorgabe", nicht „null Minuten" – deshalb wird Leeres zu `null`. */
  const alsZahl = (t: string): number | null => {
    const n = Number(t.trim())
    return t.trim() === '' || !Number.isFinite(n) || n < 5 || n > 480 ? null : Math.round(n)
  }
  const gueltig = STUFEN.every(({ id }) => werte[id].trim() === '' || alsZahl(werte[id]) !== null)

  return (
    <div className="border-t border-line pt-3">
      <div className="mb-1.5 flex items-baseline gap-2">
        <span className="text-[12.5px] font-semibold text-ink2">Dauer je Größe</span>
        <span className="text-[11.5px] text-muted">
          {weltId ? 'leer = Vorgabe' : 'Vorgabe – je Arbeitswelt überschreibbar'}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {STUFEN.map(({ id, text }) => (
          <label key={id} className="flex items-center gap-1.5 text-[12px]">
            <span className="text-ink2">{text}</span>
            <input
              type="number"
              min={5}
              max={480}
              disabled={!weltId || speichern.isPending}
              value={werte[id]}
              placeholder={String(DAUER_VORGABE[id])}
              onChange={(e) => setWerte((w) => ({ ...w, [id]: e.target.value }))}
              className="w-[62px] rounded-md border border-line bg-[#fafaf8] px-1.5 py-1 text-center tabular-nums disabled:opacity-60"
            />
            <span className="text-muted">min</span>
          </label>
        ))}
      </div>

      {weltId && (
        <div className="mt-2 flex items-center gap-2">
          <Button
            variant="ghost"
            disabled={speichern.isPending}
            onClick={() => {
              setWerte({ klein: '', mittel: '', gross: '', ohne: '' })
              speichern.mutate({ klein: null, mittel: null, gross: null, ohne: null })
            }}
          >
            Auf Vorgabe zurück
          </Button>
          <Button
            disabled={!gueltig || speichern.isPending}
            onClick={() =>
              speichern.mutate({
                klein: alsZahl(werte.klein),
                mittel: alsZahl(werte.mittel),
                gross: alsZahl(werte.gross),
                ohne: alsZahl(werte.ohne),
              })
            }
          >
            Dauern speichern
          </Button>
          {speichern.isError && (
            <span className="text-[11.5px] text-bad">
              {speichern.error instanceof Error ? speichern.error.message : 'Nicht gespeichert.'}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
